import type { FunctionComponent } from 'preact';
import { useCallback, useRef } from 'preact/hooks';

import type { RepeaterFunctionInvoker, TemplateFunction } from 'Components/types';
import { useCollapsible, useResizeState } from 'Core/hooks';
import { fitmentTableResponseSelector } from 'Core/selectors/fitmentSearch/index';
import { useSelector } from 'Core/store';
import { FitmentFieldValues, type ServerModel } from 'Modules/serverApi/types';
import { cloneSafe } from 'Utils/components';
import safeTemplateCall from 'Utils/safeTemplateCall';

enum layoutType {
  horizontal = 'horizontal',
  vertical = 'vertical',
}

type FitmentFieldType = {
  vehicleSpecific: boolean;
  noFitmentData: boolean;
  universalFit: boolean;
  manyFitments: boolean;
};

interface ItemParams {
  fitment: ServerModel.GenericItem;
}

type Params = {
  fields: string[];
  fieldTitles: Record<string, string>;
  items: RepeaterFunctionInvoker<ItemParams>;
  alwaysVisibleValues: RepeaterFunctionInvoker<ItemParams>;
  initiallyHiddenValues: RepeaterFunctionInvoker<ItemParams>;
  template: keyof typeof layoutType;
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  valuesCount: number;
  vehicleGroups: string;
} & FitmentFieldType;

interface Props {
  template: TemplateFunction<Params | FitmentFieldType>;
  columnBreakpoint?: number;
  alwaysShowedCount?: number;
}

const FitmentTable: FunctionComponent<Props> = ({ template, columnBreakpoint = 425, alwaysShowedCount }) => {
  const rootRef = useRef<HTMLElement>(null);

  const layout = useResizeState(
    useCallback(
      () => (window.innerWidth > columnBreakpoint ? layoutType.horizontal : layoutType.vertical),
      [columnBreakpoint],
    ),
  ) as keyof typeof layoutType;

  const { isCollapsed, toggle: toggleCollapsed } = useCollapsible(rootRef, null, true);

  const response = useSelector(fitmentTableResponseSelector);

  const vehicleGroups = response?.VehicleGroups?.join(', ') ?? '';

  if (
    !response ||
    (response.Fitment === FitmentFieldValues.VehicleSpecific &&
      !vehicleGroups &&
      (!response.Fitments?.length || !response.Fields?.length))
  ) {
    return null;
  }

  const { Fields, Fitment, Fitments } = response;

  const fitmentFieldData = {
    vehicleSpecific: Fitment === FitmentFieldValues.VehicleSpecific,
    noFitmentData: Fitment === FitmentFieldValues.NoFitmentData,
    universalFit: Fitment === FitmentFieldValues.UniversalFit,
    manyFitments: Fitment === FitmentFieldValues.ManyFitments,
  };

  if (!Fields?.length || !Fitments?.length) {
    return safeTemplateCall(template, { ...fitmentFieldData, vehicleGroups }, 'FitmentTable');
  }

  const alwaysShowedCountValue = alwaysShowedCount || (layout === layoutType.horizontal ? 21 : 11);

  const items = Fitments.map((fitment) => (templ: TemplateFunction<ItemParams>) => {
    const component = safeTemplateCall(
      templ,
      {
        fitment: Fields.reduce<ServerModel.GenericItem>((fitmentTemplate, { Name, Title }) => {
          fitmentTemplate[Title] = fitment[Name];
          return fitmentTemplate;
        }, {}),
      },
      'FitmentTableItem',
    );

    // const key = "Year|2015&Make|Ford&Model|F-150"
    const key = Object.entries(fitment)
      .map(([field, value]) => `${field}|${value}`)
      .join('&');

    return cloneSafe(component, null, { key });
  });

  return cloneSafe(
    safeTemplateCall(
      template,
      {
        fields: Fields.map((f) => f.Title),
        items,
        alwaysVisibleValues: items.slice(0, alwaysShowedCountValue),
        initiallyHiddenValues: items.slice(alwaysShowedCountValue),
        template: layout,
        ...fitmentFieldData,
        isCollapsed,
        toggleCollapsed,
        valuesCount: Fitments.length,
        vehicleGroups,
      },
      'FitmentTable',
    ),
    rootRef,
  );
};

export default FitmentTable;
